// Single source of truth for writing to public.profiles.
// Earlier the App.tsx save block called .update({...}).eq('id', ...) which
// could silently return 0 rows if the row didn't exist, and the inline
// payload omitted or mis-shaped the customization JSONB column. This
// helper uses upsert + an explicit customization object so the user's
// choices persist across reloads.

import { supabase } from '../../supabase';

export type ProfileCustomization = {
  font?: string;
  nameStyle?: string;
  palette?: string;
  color1?: string;
  color2?: string;
  texture?: string;
  avatarDecoration?: string;
  auraMode?: string;
  auraColor1?: string;
  auraColor2?: string;
  statusMessage?: string;
  bannerType?: string | null;
  bannerValue?: string | null;
  gradientHue1?: number | null;
  gradientSat1?: number | null;
  gradientHue2?: number | null;
  gradientSat2?: number | null;
  bannerImage?: string | null;
  profileBackgroundType?: string | null;
  profileBackgroundValue?: string | null;
  profileBackgroundGradient?: unknown;
  profileBackgroundImage?: string | null;
  profileTextStyle?: unknown;
  profileBorderStyle?: unknown;
  // ---- V2 video-effect persistence (Round-6 follow-up) ----
  // Mirrors the three zustand fields on callStore.ts. The pipeline at
  // src/services/videoProcessing.ts reads these from the callStore on
  // every effect push; we keep the DB column in lockstep so the user's
  // last-picked background (None / Soft Focus / Picture) survives
  // logout + reload + multi-device. `null` (not `undefined`) is the
  // explicit "no background image" sentinel — `undefined` would be
  // stripped by JSON.stringify and the key would silently disappear
  // from the JSONB blob.
  videoEffectMode?: 'none' | 'blur' | 'picture';
  videoEffectBg?: string | null;
  videoEffectIntensity?: number;
};

export type ProfileUpdate = {
  display_name?: string;
  photo_url?: string;
  status?: string;
  presence?: string;
  role?: 'user' | 'guest' | 'admin';
  bio?: string;
  /** Legacy top-level fields. Still useful for callers that haven't migrated. */
  bannerType?: string | null;
  bannerValue?: string | null;
  gradientHue1?: number | null;
  gradientSat1?: number | null;
  gradientHue2?: number | null;
  gradientSat2?: number | null;
  bannerImage?: string | null;
  profileBackgroundType?: string | null;
  profileBackgroundValue?: string | null;
  profileBackgroundGradient?: unknown;
  profileBackgroundImage?: string | null;
  profileTextStyle?: unknown;
  profileBorderStyle?: unknown;
  /** SINGLE source of truth for the JSONB column. This is what the
   *  ProfileCustomizer writes - we keep both the JSONB blob and the
   *  legacy top-level fields in sync so existing readers don't break. */
  customization?: ProfileCustomization;
};

/**
 * updateProfile - upsert by primary key. Throws on any error (callers
 * handle the catch). The RLS policy profiles_update_self permits this
 * only when auth.uid() = $userId.
 */
export async function updateProfile(
  userId: string,
  patch: ProfileUpdate,
): Promise<void> {
  if (!userId) throw new Error('updateProfile: userId is required');

  // Build a single payload so we hit the DB in one round-trip.
  // The customization JSONB column is the new OR-of-truth; we also keep
  // legacy top-level fields populated for clients still reading those.
  const payload: Record<string, unknown> & { id: string } = {
    id: userId,
    updated_at: new Date().toISOString(),
  };

  // Prefer customization.* if caller split it that way, otherwise copy
  // the legacy top-level keys into customization as fallback so the
  // JSONB blob always reflects what the user sees in the UI.
  const custFromPatch = patch.customization ?? {};
  const mergedCustomization: ProfileCustomization = { ...custFromPatch };

  const legacyKeys: Array<keyof ProfileUpdate> = [
    'bannerType', 'bannerValue', 'gradientHue1', 'gradientSat1',
    'gradientHue2', 'gradientSat2', 'bannerImage',
    'profileBackgroundType', 'profileBackgroundValue',
    'profileBackgroundGradient', 'profileBackgroundImage',
    'profileTextStyle', 'profileBorderStyle',
  ];
  for (const k of legacyKeys) {
    if (patch[k] !== undefined && mergedCustomization[k as keyof ProfileCustomization] === undefined) {
      (mergedCustomization as Record<string, unknown>)[k as string] = patch[k];
    }
  }

  Object.assign(payload, patch);
  payload.customization = mergedCustomization;

  const { error } = await supabase
    .from('profiles')
    .upsert(payload, { onConflict: 'id' });

  if (error) throw error;
}
